import { useState } from 'react';
import { CategoryMaster } from '../../../services/apiTypes';
import {
  MANAGE_ITEM_TEMP_ID,
  MANAGE_ITEM_MODE,
} from '../../../_constants/manageConst';
import {
  deleteCategoryMaster,
  getCategoryMaster,
  postCategoryMaster,
} from '../../../services/api/categoryMaster';
import { isGetCategoryMaster } from '../../../_utils/typeGuardUtils';
import { API_CODE } from '../../../_constants/apiConst';

export const defaultCategory = {
  categoryId: '',
  displayName: '',
  categoryName: '',
  categoryNameJp: '',
  categoryName03: '',
  categoryName04: '',
  categoryName05: '',
  categoryName06: '',
  categoryName07: '',
  categoryName08: '',
  categoryName09: '',
  categoryName10: '',
  sort: 0,
  isOptionalAddition: false,
};

type useCategoryProps = {
  categoryMaster: CategoryMaster[];
  setCategoryMaster: React.Dispatch<React.SetStateAction<CategoryMaster[]>>;
  setDisplayMode: React.Dispatch<React.SetStateAction<number>>;
  setIsOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDisabledControlBtn: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  showApiError: (
    apiError: unknown,
    apiCode: string,
    onCloseCallback?: () => void,
  ) => void;
  showTypeError: (apiCode: string) => void;
};

const useCategory = ({
  categoryMaster,
  setCategoryMaster,
  setDisplayMode,
  setIsOpenDeleteModal,
  setIsDisabledControlBtn,
  setIsLoading,
  showApiError,
  showTypeError,
}: useCategoryProps) => {
  /** 操作対象の大項目 */
  const [targetCategory, setTargetCategory] =
    useState<CategoryMaster>(defaultCategory);
  /** 大項目編集モーダルの開閉状態 */
  const [isOpenCategoryEditModal, setIsOpenCategoryEditModal] = useState(false);

  /** 操作対象大項目をリセットする */
  const resetTargetCategory = () => {
    setTargetCategory(defaultCategory);
  };

  /** 大項目の追加 */
  const onClickNewCategory = () => {
    const newCategory = {
      ...defaultCategory,
      categoryId: MANAGE_ITEM_TEMP_ID.CATEGORY,
    };
    setCategoryMaster([...categoryMaster, newCategory]);
    setDisplayMode(MANAGE_ITEM_MODE.ADD);
    setTargetCategory(newCategory);
  };

  /** 大項目の新規追加 */
  const onClickCategoryAddSave = async (
    categoryName: string,
    isOptionalAddition: boolean,
  ) => {
    setIsDisabledControlBtn(true);
    setTargetCategory({ ...targetCategory, categoryName, isOptionalAddition });
    const postCategory = {
      categoryName,
      isOptionalAddition,
    };
    try {
      await postCategoryMaster(postCategory);
      // 大項目再取得
      const response = await getCategoryMaster();
      if (!isGetCategoryMaster(response)) {
        showTypeError(API_CODE.CATEGORY_MASTER);
      }
      setCategoryMaster(response);
      resetTargetCategory();
      setDisplayMode(MANAGE_ITEM_MODE.VIEW);
    } catch (error) {
      showApiError(error, API_CODE.CATEGORY_MASTER);
    } finally {
      setIsDisabledControlBtn(false);
    }
  };

  /** 大項目の新規追加キャンセル */
  const onClickCategoryAddCancel = () => {
    setCategoryMaster(
      categoryMaster.filter(
        (c) => c.categoryId !== MANAGE_ITEM_TEMP_ID.CATEGORY,
      ),
    );
    resetTargetCategory();
    setDisplayMode(MANAGE_ITEM_MODE.VIEW);
  };

  /** 大項目の編集アイコン押下 */
  const onClickCategoryEditIcon = (category: CategoryMaster) => {
    setTargetCategory(category);
    setIsOpenCategoryEditModal(true);
  };

  /** 大項目の削除アイコン押下 */
  const onClickCategoryDeleteIcon = (category: CategoryMaster) => {
    setTargetCategory(category);
    setIsOpenDeleteModal(true);
  };

  /** 削除処理実行 */
  const executeDeleteCategory = async () => {
    setIsOpenDeleteModal(false);
    setIsLoading(true);
    try {
      await deleteCategoryMaster({ categoryId: targetCategory.categoryId });
      const response = await getCategoryMaster();
      if (!isGetCategoryMaster(response)) {
        showTypeError(API_CODE.CATEGORY_MASTER);
      } else {
        setCategoryMaster(response);
      }
    } catch (error) {
      showApiError(error, API_CODE.CATEGORY_MASTER);
    } finally {
      resetTargetCategory();
      setIsLoading(false);
    }
  };

  /** 大項目の編集保存 */
  const handleCategorySave = async () => {
    try {
      setIsOpenCategoryEditModal(false);
      resetTargetCategory();
      setDisplayMode(MANAGE_ITEM_MODE.VIEW);
      // displayNameを更新するため再取得
      const response = await getCategoryMaster();
      if (!isGetCategoryMaster(response)) {
        showTypeError(API_CODE.CATEGORY_MASTER);
      }
      setCategoryMaster(response);
    } catch (error) {
      showApiError(error, API_CODE.CATEGORY_MASTER);
    }
  };

  /** 大項目の編集キャンセル */
  const handleCategoryCancel = () => {
    resetTargetCategory();
    setDisplayMode(MANAGE_ITEM_MODE.VIEW);
    setIsOpenCategoryEditModal(false);
  };

  return {
    targetCategory,
    isOpenCategoryEditModal,
    resetTargetCategory,
    onClickNewCategory,
    onClickCategoryAddSave,
    onClickCategoryAddCancel,
    onClickCategoryEditIcon,
    onClickCategoryDeleteIcon,
    executeDeleteCategory,
    handleCategorySave,
    handleCategoryCancel,
  };
};

export default useCategory;
